import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { MpAccountScopeDTO, MpPlaceDTO, MpStatusDTO, MpTerminalDTO } from '@bifeai/shared';
import { Badge, Button, Callout, Card, CardBody, CardHeader, Field, Select, Skeleton, useToast } from '@bifeai/ui';
import { Download, Link2, Search, Unlink } from 'lucide-react';
import { useEffect, useState } from 'react';
import { api, errorMessage } from '../../lib/api';

const KEY = ['mp', 'status'];

/**
 * Mercado Pago: la carnicería conecta SU cuenta (la plata entra directo ahí), crea la caja de cada
 * sucursal para el QR y elige qué posnet Point usa cada una. Con `paymentLinkOnly` (Solo bifi, en Configurar agente)
 * muestra solo la conexión: alcanza para que bifi y el tablero manden el link de pago, sin cajas ni posnets.
 */
export function MercadoPagoCard({ paymentLinkOnly = false }: { paymentLinkOnly?: boolean } = {}) {
  const qc = useQueryClient();
  const toast = useToast();
  const st = useQuery({ queryKey: KEY, queryFn: () => api.get<MpStatusDTO>('/mp/status') });
  const [busy, setBusy] = useState(false);

  // Vuelta de Mercado Pago después de autorizar.
  useEffect(() => {
    const p = new URLSearchParams(location.search).get('mp');
    if (!p) return;
    if (p === 'ok')
      toast.success(
        'Mercado Pago conectado',
        paymentLinkOnly ? 'Ya se puede mandar el link de pago de cada pedido.' : 'Ahora creá la caja de cada sucursal para cobrar con QR.',
      );
    else if (p === 'cancelado') toast.error('Conexión cancelada', 'No se autorizó a BifeAI en Mercado Pago.');
    else toast.error('No se pudo conectar', 'Probá de nuevo. Si sigue fallando, avisale a soporte.');
    const url = new URL(location.href);
    url.searchParams.delete('mp');
    history.replaceState(null, '', url.toString());
    void qc.invalidateQueries({ queryKey: ['mp'] });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Si se vuelve con "Atrás" desde Mercado Pago, el botón no queda cargando.
  useEffect(() => {
    const reset = () => setBusy(false);
    window.addEventListener('pageshow', reset);
    return () => window.removeEventListener('pageshow', reset);
  }, []);

  const s = st.data;
  const multi = (s?.scopes.length ?? 0) > 1;
  const anyConnected = s?.scopes.some((x) => x.connected) ?? false;
  return (
    <Card>
      <CardHeader
        title={
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
            Mercado Pago {anyConnected ? <Badge tone="success">Conectado</Badge> : <Badge>Sin conectar</Badge>}
          </span>
        }
        subtitle={
          paymentLinkOnly
            ? 'Para mandar el link de pago por WhatsApp (lo manda bifi o lo mandás vos desde el pedido). La plata entra directo a tu cuenta de Mercado Pago.'
            : 'Cobros automáticos: QR con el importe exacto, posnet Point y verificación de transferencias. La plata entra directo a la cuenta de Mercado Pago de quien cobra.'
        }
      />
      <CardBody>
        {!s ? (
          <Skeleton style={{ height: 80 }} />
        ) : (
          <div className="bf-form" style={{ gap: 20 }}>
            {multi && (
              <p className="bf-hint" style={{ margin: 0 }}>
                Las sucursales propias cobran con la cuenta de la carnicería. Cada franquicia conecta <strong>su</strong> cuenta: la plata de sus
                ventas va directo a ella, nunca a la de la carnicería.
              </p>
            )}
            {s.scopes.map((sc) => (
              <ScopeSection
                key={sc.branchId ?? 'main'}
                scope={sc}
                available={s.available}
                titled={multi}
                busy={busy}
                setBusy={setBusy}
                linkOnly={paymentLinkOnly}
              />
            ))}
          </div>
        )}
      </CardBody>
    </Card>
  );
}

/** Una cuenta de Mercado Pago (la de la carnicería o la de una franquicia) y sus sucursales. */
function ScopeSection({
  scope,
  available,
  titled,
  busy,
  setBusy,
  linkOnly,
}: {
  scope: MpAccountScopeDTO;
  available: boolean;
  titled: boolean;
  busy: boolean;
  setBusy: (b: boolean) => void;
  linkOnly: boolean;
}) {
  const qc = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const franchise = scope.branchId !== null;

  async function connect() {
    setBusy(true);
    setError(null);
    try {
      const { url } = await api.post<{ url: string }>('/mp/connect', { branchId: scope.branchId });
      window.location.href = url;
    } catch (err) {
      setError(errorMessage(err));
      setBusy(false);
    }
  }

  async function disconnect() {
    const lost = linkOnly ? 'el link de pago' : 'el QR y el Point';
    if (!confirm(`¿Desconectar Mercado Pago? Deja de funcionar ${lost} desde BifeAI (la plata ya cobrada queda en la cuenta).`)) return;
    await api.del(`/mp/connection${scope.branchId ? `?branchId=${scope.branchId}` : ''}`);
    await qc.invalidateQueries({ queryKey: ['mp'] });
  }

  const owner = franchise ? 'la franquicia' : 'la carnicería';
  return (
    <section className={titled ? 'bf-mp-scope' : undefined}>
      {titled && (
        <h3 className="bf-mp-scope-title">
          {franchise ? (
            <>
              {scope.title} <Badge tone="info">Franquicia</Badge>
            </>
          ) : (
            <>{scope.title} · sucursales propias</>
          )}{' '}
          {scope.connected ? <Badge tone="success">Conectada</Badge> : <Badge>Sin conectar</Badge>}
        </h3>
      )}
      {!scope.connected ? (
        <div className="bf-form">
          {scope.canManage ? (
            <>
              <ol className="bf-hw-steps">
                <li>
                  Tocá <strong>Conectar con Mercado Pago</strong> y entrá con la cuenta de Mercado Pago de {owner} (la que recibe la plata).
                </li>
                <li>Autorizá a BifeAI. No hace falta copiar claves: la conexión se guarda cifrada.</li>
                {linkOnly ? (
                  <li>Volvés acá y listo: el link de pago de cada pedido ya cobra en esa cuenta.</li>
                ) : (
                  <li>Volvés acá y creás la caja de cada sucursal (para el QR) y elegís el posnet Point si hay.</li>
                )}
              </ol>
              {!available && (
                <Callout tone="warning">
                  BifeAI está terminando de habilitar la conexión con Mercado Pago. Muy pronto vas a poder conectarla desde acá.
                </Callout>
              )}
              {error && <Callout tone="danger">{error}</Callout>}
              <div>
                <Button variant="primary" icon={<Link2 />} loading={busy} disabled={!available} onClick={() => void connect()}>
                  Conectar con Mercado Pago{franchise ? ` de ${scope.title}` : ''}
                </Button>
              </div>
            </>
          ) : (
            <p className="bf-hint" style={{ margin: 0 }}>
              La cuenta de Mercado Pago de la carnicería la conecta la casa central.
            </p>
          )}
        </div>
      ) : (
        <div className="bf-form">
          <p style={{ margin: 0 }}>
            Cuenta conectada: <strong>{scope.nickname ?? scope.mpUserId}</strong>
          </p>
          {scope.lastError && <Callout tone="warning">{scope.lastError}</Callout>}
          {!linkOnly && <BranchesQr scope={scope} />}
          {!linkOnly && scope.canManage && <PointTerminals scope={scope} />}
          {scope.canManage && (
            <div>
              <Button size="sm" variant="ghost" icon={<Unlink />} onClick={() => void disconnect()}>
                Desconectar
              </Button>
            </div>
          )}
        </div>
      )}
    </section>
  );
}

/** Caja de Mercado Pago por sucursal: con ella sale el QR con el importe y el QR fijo para imprimir. */
function BranchesQr({ scope }: { scope: MpAccountScopeDTO }) {
  return (
    <div className="bf-form" style={{ gap: 10 }}>
      <strong>Cobro con QR</strong>
      <p className="bf-hint" style={{ margin: 0 }}>
        Cada sucursal necesita su caja en Mercado Pago. Al cobrar sale en pantalla un QR con el importe exacto; también podés imprimir el QR fijo y
        pegarlo en el mostrador: al escanearlo el cliente ve el importe de la venta.
      </p>
      {scope.branches.map((b) => (
        <BranchQrRow key={b.branchId} branch={b} canManage={scope.canManage} />
      ))}
    </div>
  );
}

function BranchQrRow({ branch, canManage }: { branch: MpAccountScopeDTO['branches'][number]; canManage: boolean }) {
  const qc = useQueryClient();
  const [stateId, setStateId] = useState('');
  const [city, setCity] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Zonas y localidades con los nombres exactos que acepta Mercado Pago.
  const states = useQuery({
    queryKey: ['mp', 'places'],
    queryFn: () => api.get<MpPlaceDTO[]>('/mp/places'),
    staleTime: Infinity,
    enabled: !branch.ready && canManage,
  });
  const cities = useQuery({
    queryKey: ['mp', 'places', stateId],
    queryFn: () => api.get<MpPlaceDTO[]>(`/mp/places?stateId=${stateId}`),
    staleTime: Infinity,
    enabled: !!stateId && !branch.ready && canManage,
  });
  if (branch.ready) {
    return (
      <div className="bf-mp-branch">
        <span>
          <strong>{branch.branchName}</strong> <Badge tone="success">QR listo</Badge>
          <span className="bf-hint"> {[branch.city, branch.province].filter(Boolean).join(', ')}</span>
        </span>
        {branch.qrImageUrl && (
          <a className="bf-btn bf-btn--sm" href={branch.qrImageUrl} target="_blank" rel="noreferrer">
            <Download size={16} aria-hidden /> QR para imprimir
          </a>
        )}
      </div>
    );
  }
  if (!canManage) {
    return (
      <div className="bf-mp-branch">
        <span>
          <strong>{branch.branchName}</strong> <Badge>Sin caja</Badge>
          <span className="bf-hint"> La crea la casa central.</span>
        </span>
      </div>
    );
  }
  const province = states.data?.find((x) => x.id === stateId)?.name ?? '';
  return (
    <div className="bf-mp-branch">
      <strong>{branch.branchName}</strong>
      <div className="bf-form-row" style={{ flex: 1 }}>
        <Field label="Provincia o zona" hint="Para el Gran Buenos Aires: “Bs.As. G.B.A.” Norte, Oeste o Sur.">
          <Select
            value={stateId}
            onChange={(e) => {
              setStateId(e.target.value);
              setCity('');
            }}
          >
            <option value="">{states.isPending ? 'Cargando…' : 'Elegí'}</option>
            {states.data?.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Localidad">
          <Select value={city} disabled={!stateId} onChange={(e) => setCity(e.target.value)}>
            <option value="">{stateId && cities.isPending ? 'Cargando…' : 'Elegí'}</option>
            {cities.data?.map((c) => (
              <option key={c.id} value={c.name}>
                {c.name}
              </option>
            ))}
          </Select>
        </Field>
      </div>
      <Button
        loading={busy}
        disabled={!city || !province}
        onClick={async () => {
          setBusy(true);
          setError(null);
          try {
            qc.setQueryData(KEY, await api.put<MpStatusDTO>('/mp/pos', { branchId: branch.branchId, city, province }));
            void qc.invalidateQueries({ queryKey: ['mp', 'counter'] });
          } catch (err) {
            setError(errorMessage(err));
          } finally {
            setBusy(false);
          }
        }}
      >
        Crear caja
      </Button>
      {(error || states.error) && <p className="bf-hint bf-text-danger">{error ?? errorMessage(states.error)}</p>}
    </div>
  );
}

/** Posnets Point de Mercado Pago: se elige cuál usa cada sucursal (el importe se manda solo). */
function PointTerminals({ scope }: { scope: MpAccountScopeDTO }) {
  const qc = useQueryClient();
  const [list, setList] = useState<MpTerminalDTO[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  async function search() {
    setBusy(true);
    setError(null);
    try {
      setList(await api.get<MpTerminalDTO[]>(`/mp/terminals${scope.branchId ? `?branchId=${scope.branchId}` : ''}`));
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  }
  async function assign(t: MpTerminalDTO, branchId: string | null) {
    setError(null);
    try {
      qc.setQueryData(KEY, await api.put<MpStatusDTO>('/mp/terminals', { terminalId: t.id, branchId, name: `Point ${t.serial}` }));
      void qc.invalidateQueries({ queryKey: ['mp', 'counter'] });
      await search();
    } catch (err) {
      setError(errorMessage(err));
    }
  }
  return (
    <div className="bf-form" style={{ gap: 10 }}>
      <strong>Posnet Point</strong>
      <p className="bf-hint" style={{ margin: 0 }}>
        Si tenés un posnet Point de Mercado Pago, asignalo a una sucursal: al cobrar con “Mercado Pago Point” el importe le llega solo y la venta se
        confirma cuando se aprueba. El posnet pasa a modo “integrado”.
      </p>
      {!list &&
        scope.terminals.map((t) => (
          <div key={t.terminalId} className="bf-mp-branch">
            <span>
              <strong>{t.name}</strong> en {t.branchName} <Badge tone="success">Listo</Badge>
            </span>
          </div>
        ))}
      {list && list.length === 0 && <p className="bf-hint">No hay posnets Point en esta cuenta de Mercado Pago.</p>}
      {list?.map((t) => (
        <div key={t.id} className="bf-mp-branch">
          <span>
            Point terminado en <strong>{t.serial}</strong>
          </span>
          <Select
            aria-label="Sucursal del posnet"
            value={t.assigned?.branchId ?? ''}
            onChange={(e) => void assign(t, e.target.value || null)}
            style={{ maxWidth: 240 }}
          >
            <option value="">Sin usar en BifeAI</option>
            {scope.branches.map((b) => (
              <option key={b.branchId} value={b.branchId}>
                {b.branchName}
              </option>
            ))}
          </Select>
        </div>
      ))}
      {error && <Callout tone="danger">{error}</Callout>}
      <div>
        <Button size="sm" icon={<Search />} loading={busy} onClick={() => void search()}>
          Buscar mis posnets Point
        </Button>
      </div>
    </div>
  );
}
